import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Alert, Badge, Button, Spinner, Td, Th, Tr } from "../ui";
import PageHeader from "../shared/PageHeader";
import ImagePreviewModal from "../shared/ImagePreviewModal";
import PageContainer from "../shared/PageContainer";
import FieldRenderer, { FieldSchema } from "./FieldRenderer";
import ProductModal from "./ProductModal";
import { AvailableProduct } from "../../types";
import { ordersApi } from "../../services/api";

interface SectionSchema {
  title: string;
  fields: FieldSchema[];
}

interface FormSchema {
  type: string;
  title: string;
  sections: SectionSchema[];
  availableProducts?: AvailableProduct[];
}

const computeField = (
  expression: string,
  data: Record<string, any>
): number => {
  try {
    const tokens = expression.split(/\s*([+-])\s*/);
    let result = parseFloat(data[tokens[0]] || "0") || 0;
    for (let i = 1; i < tokens.length; i += 2) {
      const operator = tokens[i];
      const fieldName = tokens[i + 1];
      const value = parseFloat(data[fieldName] || "0") || 0;
      if (operator === "+") result += value;
      else if (operator === "-") result -= value;
    }
    return Math.round(result * 100) / 100;
  } catch {
    return 0;
  }
};

const DynamicOrderForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { type, id } = useParams<{ type?: string; id?: string }>();

  // Determine modes
  const isPrintMode = location.pathname.includes("/orders/print/");
  const isViewMode = !!id || isPrintMode; // Print mode implies view mode behavior (read only)

  const [formData, setFormData] = useState<Record<string, any>>({});
  const [schema, setSchema] = useState<FormSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [schemaError, setSchemaError] = useState<string | null>(null);

  // Product modal state
  const [showProductModal, setShowProductModal] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [productQuantities, setProductQuantities] = useState<
    Record<number, number>
  >({});
  const [productSearch, setProductSearch] = useState("");

  // Status History Logs
  const [logs, setLogs] = useState<any[]>([]);

  // Load schema from API and initialize form (or load existing order in view mode)
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      try {
        // View mode: load existing order and its schema
        if (isViewMode && id) {
          const order = await ordersApi.getOrderById(parseInt(id, 10));
          if (!order) {
            setSchemaError(`Order not found: ${id}`);
            setLoading(false);
            return;
          }

          let loadedSchema: FormSchema | null = null;

          // Standard orders use a hardcoded schema (same as client)
          if (order.type === "standard-order") {
            loadedSchema = {
              type: "standard-order",
              title: "Standard Order",
              sections: [
                {
                  title: "Order Details",
                  fields: [
                    { name: "dateRequired", label: "Date Required", type: "date" },
                    { name: "notes", label: "Additional Notes", type: "textarea" },
                  ]
                },
                {
                  title: "Products",
                  fields: [
                    { name: "products", label: "Products", type: "productList" }
                  ]
                }
              ]
            };
          } else {
            // Dynamic orders - fetch schema from API
            try {
              loadedSchema = await ordersApi.getFormSchema(order.type);
            } catch (e) {
              console.error("Failed to fetch form schema", e);
            }
          }

          // Fetch logs
          try {
            const statusLogs: any = await ordersApi.getOrderLogs(parseInt(id, 10));
            if (statusLogs) setLogs(statusLogs);
          } catch (e) {
            console.error("Failed to load logs", e);
          }

          if (!loadedSchema) {
            setSchemaError(`Form schema not found for type: ${order.type}`);
            setLoading(false);
            return;
          }

          setSchema(loadedSchema);
          setSchemaError(null);

          // Parse stored form data JSON
          if (order.formData) {
            try {
              const parsedFormData = JSON.parse(order.formData);
              setFormData(parsedFormData);
            } catch {
              // Fallback: initialize with basic order data
              setFormData({
                clientId: order.clientId,
                type: order.type,
                dateOrdered: order.dateOrdered,
                dateRequired: order.dateRequired,
                total: order.total,
                status: order.status,
              });
            }
          }
        } else {
          // Create mode: load schema and initialize empty form
          if (!type) {
            setSchemaError("Order type not specified");
            setLoading(false);
            return;
          }

          const loadedSchema = await ordersApi.getFormSchema(type);

          if (!loadedSchema) {
            setSchemaError(`Form schema not found for type: ${type}`);
            setLoading(false);
            return;
          }

          // Dynamic Product Loading Overrides
          // Check if schema uses product list and fetch from API if so
          const hasProductList = loadedSchema.sections.some((section: SectionSchema) =>
            section.fields.some(
              (field: FieldSchema) =>
                field.type === "productList" || field.type === "inlineProductList"
            )
          );

          if (hasProductList) {
            try {
              // Admin does not need to fetch dynamic products because it's only View mode.
              loadedSchema.availableProducts = [];
            } catch (err) {
              console.error("Failed to set dynamic products", err);
            }
          }

          setSchema(loadedSchema);
          setSchemaError(null);

          // Initialize form data with default values
          const initialData: Record<string, any> = {};
          loadedSchema.sections.forEach((section: SectionSchema) => {
            section.fields.forEach((field: FieldSchema) => {
              if (field.defaultValue !== undefined) {
                initialData[field.name] = field.defaultValue;
              } else if (field.type === "quantity") {
                initialData[field.name] = 1;
              } else {
                initialData[field.name] = "";
              }
            });
          });
          setFormData(initialData);
        }
      } catch (error) {
        console.error("Failed to load data:", error);
        setSchemaError("Failed to load data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [type, id, isViewMode]);

  // SignalR connection removed

  // Computed fields
  useEffect(() => {
    if (!schema) return;

    const newFormData = { ...formData };
    let changed = false;

    schema.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.computed) {
          const computedValue = computeField(field.computed, formData);
          if (newFormData[field.name] !== computedValue) {
            newFormData[field.name] = computedValue;
            changed = true;
          }
        }
      });
    });

    if (changed) setFormData(newFormData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    schema,
    formData.price,
    formData.extra,
    formData.delivery,
    formData.deposit,
    computeField,
  ]);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    try {
      // Get required date from form data (different schemas use different field names)
      const requiredDateValue = formData.requiredDate || formData.dateRequired;

      // Build order object for API
      const orderPayload = {
        clientId: formData.branch || formData.clientId || "Client Branch",
        type: schema?.type || type || "unknown", // Store slug for direct schema lookup
        dateRequired: requiredDateValue
          ? new Date(requiredDateValue).toISOString()
          : new Date().toISOString(),
        total: formData.total || formData.balance || 0,
        status: "Pending",
        formData: JSON.stringify(formData), // Store full form data as JSON
      };

      const result = false; // dummy for compilation
      if (result) {
        // SignalR notification is handled server-side in OrdersController
        navigate("/orders");
      } else {
        setSubmitError("Failed to create order. Please try again.");
      }
    } catch (error) {
      console.error("Order creation error:", error);
      setSubmitError("An error occurred while creating the order.");
    } finally {
      setSubmitting(false);
    }
  };

  const addProductsFromModal = () => {
    if (!schema?.availableProducts) return;

    const newProducts = [...(formData.products || [])];
    schema.availableProducts.forEach((product, index) => {
      const qty = productQuantities[index];
      if (qty && qty > 0) {
        newProducts.push({ ...product, quantity: qty });
      }
    });

    setFormData((prev) => ({ ...prev, products: newProducts }));
    setProductQuantities({});
    setProductSearch("");
    setShowProductModal(false);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex min-h-64 items-center justify-center">
        <Spinner size="size-8" />
      </div>
    );
  }

  // Schema not found or error
  if (!schema || schemaError) {
    return (
      <PageContainer>
        <PageHeader title="Manage" subTitle="Orders" />
        <section className="panel space-y-4 p-5">
          <Alert tone="warning">
            {schemaError || `Form not found for order type: ${type}`}
          </Alert>
          <Button variant="outline" onClick={() => navigate("/orders")}>
            Back to Orders
          </Button>
        </section>
      </PageContainer>
    );
  }

  // Handle print preview
  const handlePrintPreview = () => {
    // Open specialized print route in new tab
    window.open(`/orders/print/${id}`, "_blank");
  };

  // Immediate print for print mode page
  const handlePrint = () => {
    window.print();
  };

  return (
    <React.Fragment>
      {!isPrintMode ? (
        <PageContainer>
          <PageHeader title="Manage" subTitle="Orders" />

          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold tracking-tight text-zinc-900">
              {isViewMode ? `View: ${schema.title}` : schema.title}
            </h2>
            {isViewMode && (
              <Button variant="outline" onClick={handlePrintPreview}>
                <i className="bi bi-printer" />
                Print Preview
              </Button>
            )}
          </div>

          {/* Form content */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {schema.sections.map((section, sectionIdx) => (
              <section key={sectionIdx} className="panel overflow-hidden">
                <h3 className="border-hairline/60 border-b px-5 py-4 text-sm font-semibold text-zinc-900">
                  {section.title}
                </h3>
                <div className="divide-hairline/50 divide-y">
                  {section.fields.map((field, fieldIdx) => (
                    <div
                      key={fieldIdx}
                      className={`grid gap-2 px-5 py-4 md:grid-cols-[12rem_1fr] md:gap-4 ${
                        field.type === "textarea"
                          ? "md:items-start"
                          : "md:items-center"
                      }`}
                    >
                      <label className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
                        {field.label}
                      </label>
                      <div className="min-w-0">
                        <FieldRenderer
                          field={field}
                          value={formData[field.name] ?? ""}
                          formData={formData}
                          setFormData={setFormData}
                          onShowProductModal={() => setShowProductModal(true)}
                          availableProducts={schema.availableProducts}
                          readOnly={isViewMode}
                          onShowImagePreview={(src) => setPreviewImage(src)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}

            {submitError && !isViewMode && (
              <Alert tone="danger">{submitError}</Alert>
            )}

            <div className="flex items-center gap-2">
              {!isViewMode && (
                <Button
                  type="submit"
                  variant="primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Spinner size="size-4" className="text-white" />
                      Saving…
                    </>
                  ) : (
                    "Save"
                  )}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/orders")}
                disabled={submitting}
              >
                Back
              </Button>
            </div>
          </form>

          <ProductModal
            show={showProductModal} onClose={() => setShowProductModal(false)}
            availableProducts={schema.availableProducts || []} productQuantities={productQuantities}
            setProductQuantities={setProductQuantities} productSearch={productSearch}
            setProductSearch={setProductSearch} onAddProducts={addProductsFromModal}
          />

          {/* Status History Section (View Mode Only) */}
          {isViewMode && (
            <section className="panel overflow-hidden">
              <h3 className="border-hairline/60 border-b px-5 py-4 text-sm font-semibold text-zinc-900">
                Order History
              </h3>
              {/* @ts-ignore */}
              {logs && logs.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        <Th>Activity</Th>
                        <Th>Done by</Th>
                        <Th>Date Time</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* @ts-ignore */}
                      {logs.map((log: any) => {
                        const status = log.newStatus || "";
                        let tone: "neutral" | "brand" | "success" | "warning" | "danger" =
                          "neutral";
                        if (status.includes("Confirmed")) tone = "success";
                        else if (status.includes("Production")) tone = "warning";
                        else if (status.includes("Completed")) tone = "success";
                        else if (status.includes("Dispatched")) tone = "brand";
                        else if (status.includes("Cancelled")) tone = "danger";

                        return (
                          <Tr key={log.id}>
                            <Td>
                              <Badge tone={tone}>{log.newStatus}</Badge>
                            </Td>
                            <Td>
                              <span className="inline-flex items-center gap-2">
                                <i className="bi bi-person-fill text-zinc-400" />
                                {log.changedBy}
                              </span>
                            </Td>
                            <Td className="text-zinc-500">
                              <span className="inline-flex items-center gap-2">
                                <i className="bi bi-calendar-event text-zinc-400" />
                                {new Date(log.changeDate).toLocaleString("en-GB", {
                                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit', hour12: true
                                })}
                              </span>
                            </Td>
                          </Tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="px-5 py-10 text-center text-sm text-zinc-500">
                  No status changes recorded yet.
                </p>
              )}
            </section>
          )}
        </PageContainer>
      ) : (
        <div id="printable-order-details" className="bg-white">
          {/* no-print keeps this toolbar off the printed page — see custom.css */}
          <div className="no-print border-hairline bg-canvas sticky top-0 mb-6 flex items-center justify-between gap-3 border-b px-5 py-3">
            <h2 className="text-base font-semibold tracking-tight text-zinc-900">
              Print Preview: {schema.title}
            </h2>
            <div className="flex items-center gap-2">
              <Button variant="primary" onClick={handlePrint}>
                <i className="bi bi-printer" />
                Print Now
              </Button>
              <Button variant="outline" onClick={() => window.close()}>
                Close
              </Button>
            </div>
          </div>

          <h1 className="mb-6 text-center text-xl font-semibold tracking-tight text-zinc-900">
            {schema.title}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6 px-5 pb-8">
            {schema.sections.map((section, sectionIdx) => (
              <section key={sectionIdx}>
                <h3 className="border-hairline mb-3 border-b pb-2 text-sm font-semibold text-zinc-900">
                  {section.title}
                </h3>
                <div className="space-y-3">
                  {section.fields.map((field, fieldIdx) => (
                    <div
                      key={fieldIdx}
                      className={`grid gap-2 md:grid-cols-[12rem_1fr] md:gap-4 ${
                        field.type === "textarea"
                          ? "md:items-start"
                          : "md:items-center"
                      }`}
                    >
                      <label className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
                        {field.label}
                      </label>
                      <div className="min-w-0">
                        <FieldRenderer
                          field={field} value={formData[field.name] ?? ""} formData={formData}
                          setFormData={setFormData} onShowProductModal={() => setShowProductModal(true)}
                          availableProducts={schema.availableProducts} readOnly={isViewMode}
                          onShowImagePreview={(src) => setPreviewImage(src)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </form>
        </div>
      )}
      <ImagePreviewModal
        show={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageSrc={previewImage || ""}
      />
    </React.Fragment>
  );
};

export default DynamicOrderForm;
